import {
  AmenityGroup,
  ExtraEquipmentAmenityType,
  extraEquipmentAmenityTypes,
  UtilityRoomAmenityType,
  utilityRoomAmenityTypes,
} from "@instacover/helpers"
import { StrictOmit } from "@instacover/utils"
import { UpdateSpec } from "dexie"

import { Amenity, Tables } from "@/database/helpers/tables"
import { Repository } from "@/database/helpers/types"
import { logger } from "@/utils/logging"
import { PartialBy } from "@/utils/types"

type UpsertData = PartialBy<Parameters<Tables["amenity"]["add"]>[0], "createdAt">

type UpdateParams = {
  amenityId: string
  data: StrictOmit<UpdateSpec<Amenity>, "dexieId">
}

type DeleteParams = {
  sessionId: string
  amenityId: string
}

export class AmenityRepository extends Repository {
  async add(data: UpsertData) {
    const currentDate = new Date()
    await this.dexie.amenity.add({
      ...data,
      createdAt: data.createdAt ?? currentDate,
    })
  }

  async bulkAdd(data: UpsertData[]) {
    const currentDate = new Date()
    const dataWithDates = data.map((item) => ({
      ...item,
      createdAt: item.createdAt ?? currentDate,
    }))

    await this.dexie.amenity.bulkAdd(dataWithDates)
  }

  async upsert(data: UpsertData) {
    const record = await this.dexie.amenity.where("id").equals(data.id).first()
    if (record) {
      await this.dexie.amenity.update(record.dexieId, { ...data })
      return
    }

    await this.add(data)
  }

  async get(id: string) {
    return await this.dexie.amenity.where({ id }).first()
  }

  async getBySessionId(instahomeSessionId: string) {
    return await this.dexie.amenity.where({ instahomeSessionId }).toArray()
  }

  async getByAmenityType(instahomeSessionId: string, amenityType: string) {
    return await this.dexie.amenity
      .where({ instahomeSessionId })
      .and((amenity) => amenity.type === amenityType)
      .first()
  }

  /* TODO prist na lepsi sposob otypovania ako type casting */
  async getByAmenityGroup<TAmenityTypes>(
    instahomeSessionId: string,
    amenityGroup: AmenityGroup,
  ): Promise<Amenity<TAmenityTypes>[]> {
    if (amenityGroup === "UtilityRoom") {
      return (await this.dexie.amenity
        .where({ instahomeSessionId })
        .and((amenity) => utilityRoomAmenityTypes.includes(amenity.type as UtilityRoomAmenityType))
        .and((amenity) => !amenity.deletedAt)
        .toArray()) as Amenity<TAmenityTypes>[]
    }

    return (await this.dexie.amenity
      .where({ instahomeSessionId })
      .and((amenity) => extraEquipmentAmenityTypes.includes(amenity.type as ExtraEquipmentAmenityType))
      .and((amenity) => !amenity.deletedAt)
      .toArray()) as Amenity<TAmenityTypes>[]
  }

  async update(params: UpdateParams) {
    const { amenityId, data } = params

    const amenity = await this.dexie.amenity.where("id").equals(amenityId).first()
    if (!amenity) {
      logger.warn(`Failed to update amenity. Amenity with id ${amenityId} not found`)
      return
    }

    await this.dexie.amenity.update(amenity.dexieId, { ...data, updatedAt: new Date() })
  }

  async markAsDeleted(params: DeleteParams) {
    const { sessionId, amenityId } = params

    const amenity = await this.dexie.amenity
      .where("id")
      .equals(amenityId)
      .and((amenity) => amenity.instahomeSessionId === sessionId)
      .first()

    if (!amenity) {
      logger.warn(`Failed to delete amenity. Amenity with id ${amenityId} not found`)
      return
    }

    this.dexie.transaction("rw", this.dexie.amenity, this.dexie.photo, async () => {
      // Mark amenity as deleted
      await this.dexie.amenity.update(amenity.dexieId, { deletedAt: new Date() })

      // Mark all photos associated with the amenity as deleted
      const amenityPhotos = await this.dexie.photo
        .where("amenityId")
        .equals(amenityId)
        .and((photo) => photo.instahomeSessionId === sessionId)
        .toArray()

      for (const photo of amenityPhotos) {
        await this.dexie.photo.update(photo.dexieId, { deleted: true, updatedAt: new Date() })
      }
    })
  }

  async deleteAll() {
    await this.dexie.amenity.clear()
  }

  /**
   * Deletes all amenities and replaces them with the provided ones.
   *
   * !!! Does not delete photos associated with the deleted amenities !!!
   */
  async replace(params: UpsertData[]) {
    await this.dexie.transaction("rw", this.dexie.amenity, async () => {
      await this.deleteAll()
      await this.bulkAdd(params)
    })
  }
}
